'use client';
import { useRef } from 'react';

const useScroll = () => {
	const originalOverflow = useRef<string | null>(null);
	const originalPaddingRight = useRef<string | null>(null);
	const scrollBarWidth = useRef<number | null>(null);
	const enableScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isDisabled = useRef<boolean>(false);

	function disableScroll() {
		if (enableScrollTimeout.current != null) {
			clearTimeout(enableScrollTimeout.current);
			enableScrollTimeout.current = null;
		}

		if (!isDisabled.current) {
			// Save the original values only the first time.
			const computedStyle = window.getComputedStyle(document.body);
			originalOverflow.current = computedStyle.overflow;
			originalPaddingRight.current = computedStyle.paddingRight;
			scrollBarWidth.current = window.innerWidth - document.documentElement.clientWidth;
			isDisabled.current = true;
		}

		document.body.style.overflow = 'hidden';
		document.body.style.paddingRight = `${scrollBarWidth.current}px`;
		document.body.classList.add('body--scrollbar-hidden');
	}

	function enableScroll() {
		if (!isDisabled.current) {
			return;
		}

		if (enableScrollTimeout.current != null) {
			clearTimeout(enableScrollTimeout.current);
		}

		enableScrollTimeout.current = setTimeout(() => {
			if (originalOverflow.current !== null) {
				document.body.style.overflow = originalOverflow.current;
			} else {
				document.body.style.removeProperty('overflow');
			}

			if (originalPaddingRight.current !== null) {
				document.body.style.paddingRight = originalPaddingRight.current;
			} else {
				document.body.style.removeProperty('padding-right');
			}

			document.body.classList.remove('body--scrollbar-hidden');

			originalOverflow.current = null;
			originalPaddingRight.current = null;
			enableScrollTimeout.current = null;
			isDisabled.current = false;
		}, 300);
	}

	return { disableScroll, enableScroll };
};

export { useScroll };
